import React, {useState, useEffect} from "react"
import {Box, Grid, Paper, Typography, CircularProgress, Chip, IconButton, Menu, MenuItem, Tooltip} from "@mui/material"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import ArchiveIcon from "@mui/icons-material/Archive"
import Autolink from "autolinker"
import styled from "styled-components"

import {NoteForm, Form, Field, FieldAnswered, Upload} from "./NoteForm"

import {api} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"

const localTimeOptions = {
	year: "numeric",
	month: "short",
	day: "numeric",
	hour: "numeric",
	minute: "numeric",
	hour12: true,
	timeZoneName: "short",
} as const

function toLocalTime(dateString: string) {
	return new Intl.DateTimeFormat("default", localTimeOptions).format(new Date(dateString))
}

type NoteStatus = {
	ID: string
	Label: string
}

type NoteArchiveStatus = {
	ID: string
	Label: string
}

type Note = {
	ID: string
	Label?: string
	CustomerID: string
	Form: {
		Type: string
		Version: number
		Fields: FieldAnswered[]
	}
	Uploads: Upload[]
	AgentName: string
	AgentEmail: string
	Status?: NoteStatus
	ArchiveStatuses?: NoteArchiveStatus[]
	CreatedAt: string
}

type NotesResponse = {
	Forms: Form[]
	Notes: Note[]
	AttachmentsInNotesEnabled: boolean
}

const isTextareaField = (field: Field | FieldAnswered) => field.Type === "textarea"
const isBooleanField = (field: Field | FieldAnswered) => field.Type === "boolean"
const isDateField = (field: Field | FieldAnswered) => field.Type === "date"
const isURLField = (field: Field | FieldAnswered) => field.Type === "url"

const isSafeHTTPURL = (value: string) => {
	try {
		const u = new URL(value)
		return u.protocol === "http:" || u.protocol === "https:"
	} catch {
		return false
	}
}

const dateDisplayOptions = {year: "numeric", month: "short", day: "numeric"} as const

function formatDate(value: string) {
	const parsed = new Date(value)
	if (Number.isNaN(parsed.getTime())) return value
	return new Intl.DateTimeFormat("default", dateDisplayOptions).format(parsed)
}

function NoteItemCard({
	note,
	attachmentsEnabled,
	onArchived,
	alwaysShowFieldName = false,
}: {
	note: Note
	attachmentsEnabled: boolean
	onArchived?: () => void
	alwaysShowFieldName?: boolean
}) {
	const {notification} = useNotification()
	const [archiveAnchor, setArchiveAnchor] = useState<HTMLElement | null>(null)
	const [archiving, setArchiving] = useState(false)
	const hasUploads = attachmentsEnabled && note.Uploads?.length > 0
	const isArchived = note.Status && note.Status.ID !== "active"
	const canArchive = !isArchived && note.ArchiveStatuses && note.ArchiveStatuses.length > 0

	const handleArchive = async (statusID: string) => {
		setArchiveAnchor(null)
		setArchiving(true)
		try {
			await api.customer.archiveNote(note.CustomerID, note.ID, {Status: statusID})
			notification.success("Note archived")
			if (onArchived) onArchived()
		} catch (error: any) {
			notification.error(error.Message || "Failed to archive note")
		} finally {
			setArchiving(false)
		}
	}

	const handleDownloadUpload = (upload: Upload) => {
		window.open(api.customer.getNoteUploadContentURL(note.CustomerID, note.ID, upload.ID), "_blank")
	}

	const renderFieldContent = (field: FieldAnswered) => {
		if (isTextareaField(field)) {
			return (
				<NoteTypography
					variant="body2"
					dangerouslySetInnerHTML={{__html: Autolink.link(String(field.Content))}}
				/>
			)
		}
		if (isBooleanField(field)) {
			return <Typography variant="body2">{field.Content ? "Yes" : "No"}</Typography>
		}
		if (isDateField(field)) {
			return <Typography variant="body2">{formatDate(String(field.Content))}</Typography>
		}
		if (isURLField(field)) {
			const value = String(field.Content)
			if (isSafeHTTPURL(value)) {
				return (
					<Typography variant="body2">
						<a href={value} target="_blank" rel="noopener noreferrer">
							{value}
						</a>
					</Typography>
				)
			}
			return <Typography variant="body2">{value}</Typography>
		}
		return <Typography variant="body2">{String(field.Content)}</Typography>
	}

	return (
		<Paper elevation={2} sx={{position: "relative", overflow: "hidden"}}>
			{isArchived && <ArchivedOverlay />}
			<Box p={4}>
				<Grid direction="column" container spacing={2}>
					<Grid container justifyContent="space-between" alignItems="center">
						<Grid>
							<Box display="flex" alignItems="center">
								<Typography color="textSecondary" variant="subtitle2">
									{note.AgentName} · <time>{toLocalTime(note.CreatedAt)}</time>
								</Typography>
								{isArchived && (
									<Box ml={1}>
										<Chip label={note.Status!.Label} size="small" color="warning" />
									</Box>
								)}
							</Box>
						</Grid>
						<Grid>
							<Box display="flex" alignItems="center" gap={1}>
								{(note.Label || note.Form.Type) !== "generic" && (
									<Chip label={note.Label || note.Form.Type} size="small" variant="outlined" />
								)}
								{canArchive && (
									<>
										<Tooltip title="Archive note">
											<IconButton
												size="small"
												onClick={(e) => setArchiveAnchor(e.currentTarget)}
												disabled={archiving}
											>
												{archiving ? (
													<CircularProgress size={16} />
												) : (
													<ArchiveIcon fontSize="small" />
												)}
											</IconButton>
										</Tooltip>
										<Menu
											anchorEl={archiveAnchor}
											open={Boolean(archiveAnchor)}
											onClose={() => setArchiveAnchor(null)}
										>
											{note.ArchiveStatuses!.map((status) => (
												<MenuItem key={status.ID} onClick={() => handleArchive(status.ID)}>
													{status.Label}
												</MenuItem>
											))}
										</Menu>
									</>
								)}
							</Box>
						</Grid>
					</Grid>

					{isArchived ? (
						<Grid>
							<Typography variant="body2" color="textSecondary" fontStyle="italic">
								Not Available
							</Typography>
						</Grid>
					) : (
						note.Form.Fields.map((field, idx) => (
							<Grid key={idx}>
								{(alwaysShowFieldName || note.Form.Fields.length > 1) && (
									<Typography variant="caption" color="textSecondary">
										{field.Name}
									</Typography>
								)}
								{renderFieldContent(field)}
							</Grid>
						))
					)}

					{hasUploads && (
						<Grid>
							<Typography variant="caption" color="textSecondary">
								Attachments:
							</Typography>
							<Box mt={1}>
								{note.Uploads.map((upload) => (
									<Box key={upload.ID} display="inline-block" mr={1}>
										<Chip
											label={upload.FileName}
											size="small"
											icon={<AttachFileIcon />}
											onClick={isArchived ? undefined : () => handleDownloadUpload(upload)}
											sx={isArchived ? {cursor: "default", opacity: 0.6} : undefined}
										/>
									</Box>
								))}
							</Box>
						</Grid>
					)}
				</Grid>
			</Box>
		</Paper>
	)
}

const NoteTypography = styled(Typography)`
	white-space: break-spaces;

	a {
		word-break: break-all;
	}
`

const ArchivedOverlay = styled.div`
	position: absolute;
	inset: 0;
	background-color: rgba(0, 0, 0, 0.04);
	pointer-events: none;
	z-index: 1;
`

function NotesV2({
	customerID,
	reload,
	systemOfRecord,
}: {
	customerID: string
	reload: () => void
	systemOfRecord: string
}) {
	const {notification} = useNotification()
	const [loading, setLoading] = useState(true)
	const [notesData, setNotesData] = useState<NotesResponse | null>(null)

	const isLatitude = systemOfRecord === "Latitude"

	const loadNotes = () => {
		setLoading(true)
		api.customer
			.getNotes(customerID)
			.then((data) => {
				setNotesData(data)
			})
			.catch(() => {
				notification.error("Failed to load notes")
			})
			.finally(() => {
				setLoading(false)
			})
	}

	useEffect(() => {
		let isMounted = true

		const loadNotesWithGuard = () => {
			setLoading(true)
			api.customer
				.getNotes(customerID)
				.then((data) => {
					if (isMounted) {
						setNotesData(data)
					}
				})
				.catch(() => {
					if (isMounted) {
						notification.error("Failed to load notes")
					}
				})
				.finally(() => {
					if (isMounted) {
						setLoading(false)
					}
				})
		}

		loadNotesWithGuard()

		return () => {
			isMounted = false
		}
	}, [customerID, notification])

	const handleNoteAdded = () => {
		loadNotes()
		reload()
	}

	if (loading) {
		return (
			<Box p={3} display="flex" justifyContent="center">
				<CircularProgress />
			</Box>
		)
	}

	if (!notesData) {
		return (
			<Box p={3}>
				<Typography color="error">Failed to load notes</Typography>
			</Box>
		)
	}

	return (
		<Box p={3}>
			<NoteForm
				customerID={customerID}
				forms={notesData.Forms}
				disabled={isLatitude}
				onSuccess={handleNoteAdded}
				attachmentsEnabled={notesData.AttachmentsInNotesEnabled}
			/>

			<Typography variant="subtitle1">{notesData.Notes.length === 0 ? "No notes" : "Notes"}</Typography>
			<Grid direction="column" spacing={2} container>
				{notesData.Notes.map((note, idx) => (
					<Grid key={idx}>
						<NoteItemCard
							note={note}
							attachmentsEnabled={notesData.AttachmentsInNotesEnabled}
							onArchived={loadNotes}
						/>
					</Grid>
				))}
			</Grid>
		</Box>
	)
}

export {NotesV2, NoteItemCard}
export type {Note, NotesResponse}
